import React, { useState, useRef, useEffect } from "react";
import { flushSync } from "react-dom";
import ReactMarkdown from 'react-markdown';
import generatePDF from 'react-to-pdf';
import { saveAs } from 'file-saver';
import { NavLink } from "react-router-dom";
import authHeader from "../services/auth-header";
import { API_BASE_URL } from "../common/config";
import useAxiosFetch from "../common/useAxiosFetch";
import LoadPage from "../common/LoadPage";
import AnimatedLoader from "../../img/AnimatedLoader";
import remarkGfm from 'remark-gfm';
import { Button } from "react-bootstrap";
import { useAppContext } from "../AppContext";
import { useTranslation } from "react-i18next";
import { MODAL_TYPES } from "../modals/ModalManager";
import moment from "moment";
import "./CoPilotPage.css";

// Category configuration for styling
const CATEGORY_STYLES = {
    'Risk Discovery': { icon: 'icon-risk-review', color: '#e74c3c', gradientClass: 'copilot-gradient-risks' },
    'Risico Ontdekking': { icon: 'icon-risk-review', color: '#e74c3c', gradientClass: 'copilot-gradient-risks' },
    'Risk Analysis': { icon: 'icon-Risico', color: '#e74c3c', gradientClass: 'copilot-gradient-risks' },
    'Risico Analyse': { icon: 'icon-Risico', color: '#e74c3c', gradientClass: 'copilot-gradient-risks' },
    'Measure Analysis': { icon: 'icon-Maatregelen', color: '#3498db', gradientClass: 'copilot-gradient-measures' },
    'Maatregel Analyse': { icon: 'icon-Maatregelen', color: '#3498db', gradientClass: 'copilot-gradient-measures' },
    'Trends': { icon: 'icon-trend', color: '#9b59b6', gradientClass: 'copilot-gradient-trends' },
    'Trend': { icon: 'icon-trend', color: '#9b59b6', gradientClass: 'copilot-gradient-trends' },
    'Compliance': { icon: 'icon-certificate', color: '#27ae60', gradientClass: 'copilot-gradient-compliance' },
    'Threat Intelligence': { icon: 'icon-Risico', color: '#e74c3c', gradientClass: 'copilot-gradient-risks' },
    'Dreigingsinformatie': { icon: 'icon-Risico', color: '#e74c3c', gradientClass: 'copilot-gradient-risks' },
};

const DEFAULT_STYLE = { icon: 'icon-stars1', color: '#6c757d', gradientClass: 'copilot-gradient-risks' };

const STANDARD_LABELS = {
    'ISO27001': 'ISO 27001',
    'NEN7510': 'NEN 7510',
    'ISO42001': 'ISO 42001',
};

// Replace hardcoded standard name references in display text with the org's actual standard
const replaceStandardInText = (text, securityStandard) => {
    if (!text || !securityStandard) return text;
    const label = STANDARD_LABELS[securityStandard] || STANDARD_LABELS['ISO27001'];
    return text
        .replace(/ISO\s?27001(\s*\(2022\))?/gi, label)
        .replace(/NEN\s?7510/gi, label)
        .replace(/ISO\s?42001/gi, label);
};

// Get question metadata from database object with language support
const getQuestionMeta = (question, language, securityStandard) => {
    const isNL = language === 'nl';
    return {
        category: isNL ? (question.categoryNL || question.category) : question.category,
        shortTitle: replaceStandardInText(isNL ? (question.shortTitleNL || question.shortTitle) : question.shortTitle, securityStandard),
        description: replaceStandardInText(isNL ? (question.descriptionNL || question.description) : question.description, securityStandard),
    };
};

// Get category styling based on category name
const getCategoryStyle = (categoryName) => {
    return CATEGORY_STYLES[categoryName] || DEFAULT_STYLE;
};

const CoPilotPage = () => {
    const { t, i18n } = useTranslation();
    const { data: questionData, loading } = useAxiosFetch("/api/copilot?enabled=true");
    const { data: featureLimits, refetch: refetchLimits } = useAxiosFetch("/api/features/limits");

    const { currentOrg, currentUser, openModal } = useAppContext();
    const [data, setData] = useState(null);
    const [activeQuestion, setActiveQuestion] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const [isStreaming, setStreaming] = useState(false);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [pdfExportedAt, setPdfExportedAt] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const abortController = useRef(null);
    const resultRef = useRef(null);
    const resultBodyRef = useRef(null);
    const exportRef = useRef(null);

    // Refresh AI usage every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            refetchLimits();
        }, 30000);
        return () => clearInterval(interval);
    }, [refetchLimits]);

    // Scroll to results when loading starts
    useEffect(() => {
        if (isLoading && resultRef.current) {
            resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [isLoading]);

    // Check if there's content below the viewport during streaming
    useEffect(() => {
        if (!isStreaming && !data) {
            setShowScrollButton(false);
            return;
        }
        
        const checkOverflow = () => {
            if (resultBodyRef.current) {
                const rect = resultBodyRef.current.getBoundingClientRect();
                const isOverflowing = rect.bottom > window.innerHeight + 100;
                setShowScrollButton(isOverflowing);
            }
        };
        
        checkOverflow();
        
        // Also check on scroll
        const handleScroll = () => checkOverflow();
        window.addEventListener('scroll', handleScroll);
        
        return () => window.removeEventListener('scroll', handleScroll);
    }, [data, isStreaming]);

    const getAdvice = async (question) => {
        const currentAiUsage = featureLimits ? {
            current: featureLimits.usage.aiCalls.current,
            limit: featureLimits.limits.aiCallsPerWeek
        } : null;
        
        if (currentAiUsage && currentAiUsage.limit !== -1 && currentAiUsage.current >= currentAiUsage.limit) {
            openModal(MODAL_TYPES.UPGRADE, {
                featureType: 'AI',
                currentLimit: currentAiUsage.limit,
                currentCount: currentAiUsage.current,
                recommendedPlan: 'premium'
            });
            return;
        }

        // Abort any existing request
        if (abortController.current) { 
            abortController.current.abort();
        }
        
        // Reset state for new request - show loader
        setData(null);
        setLoading(true);
        setStreaming(false);
        setActiveQuestion(question);
        
        abortController.current = new AbortController();

        try {
            const response = await fetch(`${API_BASE_URL}/api/risks/advice/${currentOrg.id}/${question.id}/${i18n.language}?standard=${encodeURIComponent(currentOrg.securityStandard || 'ISO27001')}`, { 
                headers: authHeader(), 
                signal: abortController.current.signal 
            });

            if (response.status === 403) {
                const errorData = await response.json();
                if (errorData.upgradeRequired) {
                    openModal(MODAL_TYPES.UPGRADE, {
                        featureType: 'AI',
                        currentLimit: errorData.limit,
                        currentCount: errorData.current,
                        recommendedPlan: 'premium'
                    });
                    setLoading(false);
                    return;
                }
            }

            const reader = response.body.getReader();
            let advice = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    advice += `\n\n*${t('disclaimer_txt')}*`;
                    setData(advice);
                    setLoading(false);
                    setStreaming(false);
                    refetchLimits();
                    break;
                }
                // First chunk received - switch from loading to streaming
                if (!advice) {
                    setLoading(false);
                    setStreaming(true);
                }
                advice += new TextDecoder("utf-8").decode(value);
                setData(advice);
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Fetch aborted');
            } else {
                console.error('Failed to get advice:', error);
                setLoading(false);
                setStreaming(false);
            }
        }
    };

    const handleClose = () => {
        if (abortController.current) {
            abortController.current.abort();
        }
        setData(null);
        setActiveQuestion(null);
        setLoading(false);
        setStreaming(false);
        setShowScrollButton(false);
    };

    const scrollToBottom = () => {
        if (resultBodyRef.current) {
            const bottom = resultBodyRef.current.getBoundingClientRect().bottom + window.scrollY;
            window.scrollTo({ top: bottom - window.innerHeight + 50, behavior: 'smooth' });
        }
    };

    const getReportTitle = () => activeQuestion
        ? getQuestionMeta(activeQuestion, i18n.language, currentOrg.securityStandard).shortTitle || activeQuestion.description
        : '';

    const formatExportTimestamp = (value) => moment(value).format('YYYY-MM-DD HH:mm');

    const getExportFilename = (exportedAt, extension) => {
        const slug = (getReportTitle() || 'report').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        return `${currentOrg.name.toLowerCase()}_copilot_${slug}_${moment(exportedAt).format('YYYY-MM-DD')}.${extension}`;
    };

    const handleExport = async (exportType) => {
        // Check if environment is on trial plan (no paid subscription)
        if (featureLimits?.planInfo?.planId === 'trial') {
            openModal(MODAL_TYPES.UPGRADE, { featureType: 'Export' });
            return;
        }

        const exportedAt = new Date();
        setIsExporting(true);
        try {
            if (exportType === 'pdf') {
                // Render the export header synchronously so it is part of the captured report
                flushSync(() => setPdfExportedAt(exportedAt));
                await generatePDF(exportRef, {
                    filename: getExportFilename(exportedAt, 'pdf'),
                    page: { margin: 10 },
                });
            } else if (exportType === 'word') {
                // Loaded on demand to keep the docx library out of the main bundle
                const { createCopilotReportDocx } = await import("./copilotReportDocx");
                const blob = await createCopilotReportDocx(data, {
                    title: t('copilot_ai_response', 'AI Analysis'),
                    subtitle: getReportTitle(),
                    meta: [currentOrg.name, `${t('exported_on')}: ${formatExportTimestamp(exportedAt)}`],
                });
                saveAs(blob, getExportFilename(exportedAt, 'docx'));
            }
        } catch (error) {
            console.error('Failed to export report:', error);
        } finally {
            setPdfExportedAt(null);
            setIsExporting(false);
        }
    };

    if (loading) return <LoadPage />;

    const getUsagePercentage = () => {
        if (!featureLimits) return 0;
        const limit = featureLimits.limits.aiCallsPerWeek;
        const current = featureLimits.usage.aiCalls.current;
        if (limit === -1) return 0;
        return Math.min((current / limit) * 100, 100);
    };

    const getUsageColor = () => {
        const percentage = getUsagePercentage();
        if (percentage >= 95) return '#e74c3c';
        if (percentage >= 80) return '#f39c12';
        return '#27ae60';
    };

    const aiUsage = featureLimits ? {
        current: featureLimits.usage.aiCalls.current,
        limit: featureLimits.limits.aiCallsPerWeek,
        resetsAt: featureLimits.usage.aiCalls.resetsAt
    } : null;

    return (
        <div id="content">
            {(currentUser.role === "superadmin" && currentUser.environmentId === 'main') && (
                <NavLink key="copilotmanagement" to="/copilotmanagement">
                    <Button className="add-btn btn-right-top" variant="primary" type="button" style={{width: 46}}>
                        <i className="icon-edit" />
                    </Button>
                </NavLink>
            )}

            <div className="copilot-container">
                {/* Header Section */}
                <div className="copilot-header">
                    <h1 className="copilot-title">
                        <i className="icon-stars1 copilot-title-icon" />
                        {t('copilot_title', 'Security CoPilot')}
                    </h1>
                    <p className="copilot-subtitle">
                        {t('copilot_subtitle', 'Get AI-powered insights about your security posture, risks, and compliance status')}
                    </p>
                    
                    {aiUsage && aiUsage.limit !== -1 && (
                        <div className="copilot-usage-badge">
                            <i className="icon-bolt" style={{ color: getUsageColor() }} />
                            <span>{aiUsage.current} / {aiUsage.limit} {t('copilot_calls_used', 'calls used')}</span>
                            <div className="copilot-usage-progress">
                                <div 
                                    className="copilot-usage-progress-bar"
                                    style={{ width: `${getUsagePercentage()}%`, background: getUsageColor() }} 
                                />
                            </div>
                            {aiUsage.resetsAt && (
                                <span className="copilot-usage-reset">
                                    {t('copilot_resets', 'Resets')} {moment(aiUsage.resetsAt).format('MMM D')}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Question Cards Grid */}
                {questionData && questionData.length > 0 ? (
                    <div className="copilot-grid">
                        {questionData.map((question) => {
                            const meta = getQuestionMeta(question, i18n.language, currentOrg.securityStandard);
                            const categoryStyle = getCategoryStyle(meta.category);
                            const isActive = activeQuestion?.id === question.id;
                            
                            return (
                                <div 
                                    key={question.id}
                                    className={`copilot-card ${isActive ? 'active' : ''}`}
                                    data-category={question.data}
                                    onClick={() => getAdvice(question)}
                                >
                                    <div className="copilot-card-header">
                                        <div className={`copilot-card-icon-wrapper ${categoryStyle.gradientClass}`}>
                                            <i className={`${question.icon || categoryStyle.icon} copilot-card-icon`} />
                                        </div>
                                        <div className="copilot-card-content">
                                            <div className="copilot-card-category" style={{ color: categoryStyle.color }}>
                                                {meta.category || question.data}
                                            </div>
                                            <div className="copilot-card-title">
                                                {meta.shortTitle || question.description}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="copilot-card-description">
                                        {meta.description}
                                    </div>
                                    <div className="copilot-card-arrow">
                                        <i className="icon-arrow-right" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="copilot-empty-state">
                        <i className="icon-inbox copilot-empty-state-icon" />
                        <p className="copilot-empty-state-text">{t('copilot_no_questions', 'No questions available')}</p>
                    </div>
                )}

                {/* Results Section */}
                {(isLoading || isStreaming || data) && (
                    <div className="copilot-result-container" ref={resultRef}>
                        <div className="copilot-result-header">
                            <div className="copilot-result-header-content">
                                <i className="icon-stars1 copilot-result-header-icon" />
                                <div>
                                    <div className="copilot-result-header-title">
                                        {t('copilot_ai_response', 'AI Analysis')}
                                    </div>
                                    {activeQuestion && (
                                        <div className="copilot-result-header-subtitle">
                                            {getReportTitle()}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="copilot-result-header-actions">
                                {data && !isLoading && !isStreaming && (
                                    <>
                                        <button
                                            className="copilot-close-button"
                                            onClick={() => handleExport('pdf')}
                                            disabled={isExporting}
                                            title={t('copilot_export_pdf', 'Export as PDF')}
                                        >
                                            <i className="icon-file-pdf" />
                                        </button>
                                        <button
                                            className="copilot-close-button"
                                            onClick={() => handleExport('word')}
                                            disabled={isExporting}
                                            title={t('copilot_export_word', 'Export as Word')}
                                        >
                                            <i className="icon-file-word" />
                                        </button>
                                    </>
                                )}
                                <button 
                                    className="copilot-close-button"
                                    onClick={handleClose}
                                    title={t('close', 'Close')}
                                >
                                    <i className="icon-close" />
                                </button>
                            </div>
                        </div>
                        <div className="copilot-result-body" ref={resultBodyRef}>
                            {isLoading ? (
                                <div className="copilot-loading-container">
                                    <AnimatedLoader />
                                    <p className="copilot-loading-text">
                                        {t('copilot_analyzing', 'Analyzing your security data...')}
                                    </p>
                                </div>
                            ) : (
                                <div ref={exportRef} className="copilot-result-content">
                                    {pdfExportedAt && (
                                        <div className="copilot-export-header">
                                            <div className="copilot-export-header-title">{t('copilot_ai_response', 'AI Analysis')}: {getReportTitle()}</div>
                                            <div className="copilot-export-header-meta">
                                                {currentOrg.name} • {t('exported_on')}: {formatExportTimestamp(pdfExportedAt)}
                                            </div>
                                        </div>
                                    )}
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{data}</ReactMarkdown>
                                </div>
                            )}
                        </div>
                        {showScrollButton && (
                            <button 
                                className="copilot-scroll-button"
                                onClick={scrollToBottom}
                                title={t('copilot_scroll_down', 'Scroll to see more')}
                            >
                                <i className="icon-chevron-down" />
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CoPilotPage;
